import React, { useEffect, useMemo, useState } from 'react';
import { konsinyeOzDonusum } from '../../services/stokV2Service';
import IntegerInput from '../IntegerInput';
import { parseAdet } from '../../utils/integerInput';
import './KonsinyeOzDonusumModal.css';

const formatMoney = (value) => {
  if (!Number.isFinite(value)) return '—';
  return `${value.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺`;
};

const formatBirimInput = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return '';
  return n.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const parseBirimFiyat = (raw) => {
  const t = String(raw ?? '').trim();
  if (!t) return null;
  const normalized = t.includes(',') ? t.replace(/\./g, '').replace(',', '.') : t;
  const n = parseFloat(normalized);
  return Number.isFinite(n) && n >= 0 ? n : null;
};

const KonsinyeOzDonusumModal = ({
  open,
  onClose,
  onSuccess,
  teknikServisId,
  icerikId,
  parcaAdi,
  parcaKodu,
  maxKonsinye = 0,
  netFiyat,
}) => {
  const [adet, setAdet] = useState('1');
  const [birimFiyat, setBirimFiyat] = useState('');
  const [faturaBilgisi, setFaturaBilgisi] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setAdet('1');
    setBirimFiyat(formatBirimInput(netFiyat));
    setFaturaBilgisi('');
    setError('');
    setSubmitting(false);
  }, [open, netFiyat, icerikId]);

  const parsedAdet = useMemo(() => {
    const n = parseAdet(adet, { min: 1, max: maxKonsinye || undefined });
    return Number.isFinite(n) && n > 0 ? n : 1;
  }, [adet, maxKonsinye]);

  const parsedBirim = useMemo(() => parseBirimFiyat(birimFiyat), [birimFiyat]);

  const toplamFiyat = useMemo(() => {
    if (parsedBirim == null) return null;
    return Math.round(parsedAdet * parsedBirim * 100) / 100;
  }, [parsedAdet, parsedBirim]);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (parsedBirim == null) {
      setError('Geçerli birim satış fiyatı girin');
      return;
    }
    setSubmitting(true);
    try {
      const res = await konsinyeOzDonusum({
        teknikServisId,
        icerikId,
        adet: parsedAdet,
        birimFiyat: parsedBirim,
        faturaBilgisi: faturaBilgisi.trim(),
      });
      if (res?.basarili) {
        onSuccess?.(res);
        onClose?.();
      } else {
        setError(res?.mesaj || 'İşlem başarısız');
      }
    } catch (err) {
      setError(err?.message || err?.data?.mesaj || 'İşlem başarısız');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay konsinye-oz-modal" onClick={onClose}>
      <div className="modal-content konsinye-oz-modal__panel" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header konsinye-oz-modal__header">
          <h2>Konsinyeyi öz stoka çevir</h2>
          <button type="button" className="modal-close-btn" onClick={onClose} aria-label="Kapat">
            <i className="fas fa-times" />
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <p className="konsinye-oz-modal__parca">
              <strong>{parcaAdi || '—'}</strong>
              {parcaKodu ? <code>{parcaKodu}</code> : null}
            </p>
            <p className="konsinye-oz-modal__hint">
              Mevcut konsinye: <strong>{maxKonsinye}</strong> — kargo bilgisi istenmez; fatura bilgisi zorunludur.
            </p>
            {error ? <div className="error-message">{error}</div> : null}
            <label className="form-field">
              <span>Adet</span>
              <IntegerInput
                min={1}
                max={maxKonsinye || undefined}
                value={adet}
                onChange={setAdet}
              />
            </label>
            <label className="form-field">
              <span>Birim satış fiyatı (₺)</span>
              <input
                type="text"
                inputMode="decimal"
                value={birimFiyat}
                onChange={(e) => setBirimFiyat(e.target.value)}
                required
              />
            </label>
            <div className="konsinye-oz-modal__price-summary" aria-live="polite">
              <div className="konsinye-oz-modal__price-row">
                <span>Adet × birim fiyat</span>
                <strong>
                  {parsedAdet} × {parsedBirim != null ? formatMoney(parsedBirim) : '—'}
                </strong>
              </div>
              <div className="konsinye-oz-modal__price-row konsinye-oz-modal__price-row--total">
                <span>Toplam fiyat</span>
                <strong>{toplamFiyat != null ? formatMoney(toplamFiyat) : '—'}</strong>
              </div>
            </div>
            <label className="form-field">
              <span>Fatura bilgisi</span>
              <input
                type="text"
                value={faturaBilgisi}
                onChange={(e) => setFaturaBilgisi(e.target.value)}
                placeholder="Fatura no / açıklama"
                required
              />
            </label>
          </div>
          <div className="modal-footer">
            <button type="button" className="secondary-btn" onClick={onClose} disabled={submitting}>İptal</button>
            <button type="submit" className="primary-btn" disabled={submitting}>
              {submitting ? 'Kaydediliyor…' : 'Dönüşümü uygula'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default KonsinyeOzDonusumModal;
